import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Primary core page loaded directly to avoid dynamic chunk network failures
import EventsList from './pages/EventsList';

// Resilient lazy import wrapper with automatic retry and reload recovery
function lazyRetry<T extends React.ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      console.warn('Dynamic chunk import failed, attempting recovery...', error);
      const hasRetried = sessionStorage.getItem('lazy_retry_recovery');
      if (!hasRetried) {
        sessionStorage.setItem('lazy_retry_recovery', 'true');
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
      sessionStorage.removeItem('lazy_retry_recovery');
      throw error;
    }
  });
}

// Lazy-loaded pages with resilient auto-retry
const Dashboard = lazyRetry(() => import('./pages/Dashboard'));
const PartnerPortal = lazyRetry(() => import('./pages/PartnerPortal'));
const PendingValidations = lazyRetry(() => import('./pages/PendingValidations'));
const Scanner = lazyRetry(() => import('./pages/Scanner'));
const Login = lazyRetry(() => import('./pages/Login'));
const TeamAdmin = lazyRetry(() => import('./pages/TeamAdmin'));
const MktmTeam = lazyRetry(() => import('./pages/MktmTeam'));
const InvitationAdmin = lazyRetry(() => import('./pages/InvitationAdmin'));
const SmsCampaigns = lazyRetry(() => import('./pages/SmsCampaigns'));
const PublicRSVP = lazyRetry(() => import('./pages/PublicRSVP'));
const ClientsDatabase = lazyRetry(() => import('./pages/ClientsDatabase'));
const DigitalTicket = lazyRetry(() => import('./pages/DigitalTicket'));
const PartnerSubmit = lazyRetry(() => import('./pages/PartnerSubmit'));
const DistributorSubmit = lazyRetry(() => import('./pages/DistributorSubmit'));
const DistributorsPortal = lazyRetry(() => import('./pages/DistributorsPortal'));
const ManagerGuestsPortal = lazyRetry(() => import('./pages/ManagerGuestsPortal'));
const MktmOrganizer = lazyRetry(() => import('./pages/MktmOrganizer'));
const IntegrationsSettings = lazyRetry(() => import('./pages/IntegrationsSettings'));
const UserManual = lazyRetry(() => import('./pages/UserManual'));
const PublicTermsAndPrivacy = lazyRetry(() => import('./pages/PublicTermsAndPrivacy'));

const RouteLoading = () => (
  <div className="min-h-[40vh] flex items-center justify-center p-8">
    <div className="flex items-center gap-2.5 text-slate-400 text-sm font-medium">
      <div className="w-5 h-5 border-2 border-slate-300 border-t-[#E2231A] rounded-full animate-spin" />
      <span>Se încarcă...</span>
    </div>
  </div>
);

const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false, requireMarketingAccess = false, requireSmsAccess = false, allowHostess = false }: { children: React.ReactNode, requireAdmin?: boolean, requireSuperAdmin?: boolean, requireMarketingAccess?: boolean, requireSmsAccess?: boolean, allowHostess?: boolean }) => {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center">Se încarcă...</div>;
  if (!user) return <Navigate to="/login" replace />;
  
  if (requireSuperAdmin && !user.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && user.role !== 'admin' && !(allowHostess && ((user.role as string) === 'hostess' || user.role === 'admin'))) {
    return <Navigate to="/" replace />;
  }

  if (requireMarketingAccess && !user.canAccessMarketingMaterials) {
    return <Navigate to="/" replace />;
  }

  if (requireSmsAccess && !user.canAccessSmsCampaigns) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/rsvp/:id" element={<PublicRSVP />} />
            <Route path="/c/:id" element={<PublicRSVP />} />
            <Route path="/terms/:eventId" element={<PublicTermsAndPrivacy initialType="terms" />} />
            <Route path="/privacy/:eventId" element={<PublicTermsAndPrivacy initialType="privacy" />} />
            <Route path="/legal/:eventId" element={<PublicTermsAndPrivacy />} />
            <Route path="/t/:code" element={<DigitalTicket />} />
            <Route path="/ticket/:eventId/:guestId" element={<DigitalTicket />} />
            <Route path="/submit-guests/:partnerId/:token" element={<PartnerSubmit />} />
            <Route path="/submit-resellers/:distributorId/:token" element={<DistributorSubmit />} />
            
            <Route path="/" element={
              <ProtectedRoute>
                <EventsList />
              </ProtectedRoute>
            } />

            <Route path="/events/:eventId" element={<Layout />}>
              <Route path="dashboard" element={
                <ProtectedRoute requireAdmin>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="mktm" element={
                <ProtectedRoute requireAdmin>
                  <MktmOrganizer />
                </ProtectedRoute>
              } />
              <Route path="distributors" element={
                <ProtectedRoute requireAdmin>
                  <DistributorsPortal />
                </ProtectedRoute>
              } />
              <Route path="partner" element={
                <ProtectedRoute requireAdmin>
                  <PartnerPortal />
                </ProtectedRoute>
              } />
              <Route path="guests/managers" element={
                <ProtectedRoute requireAdmin>
                  <ManagerGuestsPortal />
                </ProtectedRoute>
              } />
              <Route path="guests/:viewMode" element={
                <ProtectedRoute requireAdmin>
                  <PartnerPortal />
                </ProtectedRoute>
              } />
              <Route path="validations" element={
                <ProtectedRoute requireAdmin>
                  <PendingValidations />
                </ProtectedRoute>
              } />
              <Route path="validations/:subtab" element={
                <ProtectedRoute requireAdmin>
                  <PendingValidations />
                </ProtectedRoute>
              } />
              <Route path="scanner" element={
                <ProtectedRoute requireAdmin allowHostess>
                  <Scanner />
                </ProtectedRoute>
              } />
            <Route path="invitations" element={
              <ProtectedRoute requireAdmin requireMarketingAccess>
                <InvitationAdmin />
              </ProtectedRoute>
            } />
            <Route path="sms" element={
              <ProtectedRoute requireAdmin requireSmsAccess>
                <SmsCampaigns />
              </ProtectedRoute>
            } />
            <Route path="settings" element={
              <ProtectedRoute requireSuperAdmin>
                <IntegrationsSettings />
              </ProtectedRoute>
            } />
            <Route path="help" element={
              <ProtectedRoute requireAdmin>
                <UserManual />
              </ProtectedRoute>
            } />
            <Route path="manual" element={
              <ProtectedRoute requireAdmin>
                <UserManual />
              </ProtectedRoute>
            } />
          </Route>
          
          {/* Global Settings & Admin */}
          <Route path="/settings" element={
            <ProtectedRoute requireSuperAdmin>
              <Layout hideSidebar />
            </ProtectedRoute>
          }>
            <Route index element={<IntegrationsSettings />} />
          </Route>

          {/* User Manual & Help Center - Admin and SuperAdmin */}
          <Route path="/help" element={
            <ProtectedRoute requireAdmin>
              <Layout hideSidebar />
            </ProtectedRoute>
          }>
            <Route index element={<UserManual />} />
          </Route>
          
          <Route path="/manual" element={
            <ProtectedRoute requireAdmin>
              <Layout hideSidebar />
            </ProtectedRoute>
          }>
            <Route index element={<UserManual />} />
          </Route>
          
          {/* Team Admin is global */}
          <Route path="/team" element={
            <ProtectedRoute requireSuperAdmin>
              <Layout hideSidebar />
            </ProtectedRoute>
          }>
            <Route index element={<TeamAdmin />} />
          </Route>

          <Route path="/mktm-team" element={
            <ProtectedRoute requireSuperAdmin>
              <Layout hideSidebar />
            </ProtectedRoute>
          }>
            <Route index element={<MktmTeam />} />
          </Route>

          <Route path="/mktm" element={
            <ProtectedRoute requireSuperAdmin>
              <Layout hideSidebar />
            </ProtectedRoute>
          }>
            <Route index element={<MktmTeam />} />
          </Route>
          {/* Global Clients DB */}
          <Route path="/clients" element={
            <ProtectedRoute requireAdmin>
              <Layout hideSidebar />
            </ProtectedRoute>
          }>
            <Route index element={<ClientsDatabase />} />
          </Route>
        </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}
